import React from 'react';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

// Mirror the page wrapper used when jsm-routing-release-2 is on.
// Keep layout values in sync with styles.pageWrapperNew in WorkforceOptimizationSettingsView.tsx.
const styles = cssMap({
	container: {
		width: '100%',
		maxWidth: '1290px',
		marginInline: 'auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.1000'),
	},
	// Page heading (~28px PageHeader h1)
	heading: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '28px',
		width: '200px',
	},
	// Subtitle line under heading
	subtitle: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '16px',
		width: '540px',
	},
	// Tab bar — two tab pills side by side
	tab: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '32px',
		width: '96px',
	},
	// Status banner (full-width, matches statusBanner padding ~40px tall)
	statusBanner: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.medium'),
		height: '40px',
		width: '100%',
	},
	// Section card title
	sectionTitle: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '20px',
		width: '120px',
	},
	// Section card description
	sectionDesc: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '16px',
		width: '400px',
	},
	// Row inside a section card (title + action button placeholder)
	rowTitle: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '16px',
		width: '180px',
	},
	rowSubtitle: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '14px',
		width: '320px',
	},
	rowAction: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '32px',
		width: '120px',
		flexShrink: 0,
	},
});

// A single card row: label+subtitle on the left, action pill on the right
const SkeletonRow = (): React.JSX.Element => (
	<Inline alignBlock="center" grow="fill" space="space.200">
		<Stack space="space.050" grow="fill">
			<Box xcss={styles.rowTitle} />
			<Box xcss={styles.rowSubtitle} />
		</Stack>
		<Box xcss={styles.rowAction} />
	</Inline>
);

// A labelled section card with a title, description, and rows
const SkeletonSection = ({ rows }: { rows: number }): React.JSX.Element => (
	<Stack space="space.300">
		<Stack space="space.075">
			<Box xcss={styles.sectionTitle} />
			<Box xcss={styles.sectionDesc} />
		</Stack>
		{Array.from({ length: rows }).map((_, i) => (
			<SkeletonRow key={i} />
		))}
	</Stack>
);

export const WorkforceOptimizationSettingsSkeleton = (): React.JSX.Element => (
	<Box
		xcss={styles.container}
		aria-hidden="true"
		testId="project-settings-service-desk-workforce-optimization.loading-skeleton"
	>
		<Stack space="space.400">
			{/* Page heading + subtitle */}
			<Stack space="space.100">
				<Box xcss={styles.heading} />
				<Box xcss={styles.subtitle} />
			</Stack>

			{/* Tab bar */}
			<Inline space="space.200">
				<Box xcss={styles.tab} />
				<Box xcss={styles.tab} />
			</Inline>

			{/* Status banner */}
			<Box xcss={styles.statusBanner} />

			{/* Routing section: Connect teams + Services */}
			<SkeletonSection rows={2} />

			{/* Agent assignment section: Availability + Capacity */}
			<SkeletonSection rows={2} />
		</Stack>
	</Box>
);
